import React, { useState, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { USE_CASES, CATEGORIES, THREAT_LEVELS, COMPARE_ROWS, CAPABILITY_ROWS, PLAYBOOK } from '../data/competitionConstants';
import { useCompetitions } from '../hooks/useEntityQueries';
import { QUERY_KEYS } from '../queryClient';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const EMPTY = {
    Name: '', Category: 'FinCrime/Fraud', Threat: 'medium', CompType: 'Adjacent',
    Tagline: '', Approach: 'point', UseCases: [], Founded: '', HQ: '', Funding: '',
    Valuation: '', Employees: '', Regions: '', Pricing: '', Website: '', Tags: [],
    Description: '', Strengths: [], Weaknesses: [], CustomerPain: [], WinThemes: [],
    Objections: [], Positioning: '', ArchLimits: '', MatrixPos: '', Sectors: '',
    TechStack: '', FocusMarket: '', FocusUseCases: '', FocusSector: '',
};

const BAR_COLORS = ['#2563eb', '#7c3aed', '#059669', '#d97706', '#0891b2', '#db2777'];

export default function Competitions() {
    const queryClient = useQueryClient();
    const { data: rawCompetitors, isLoading: loading } = useCompetitions();
    const competitors = rawCompetitors || [];
    const [view, setView] = useState('dashboard');
    const [activeCat, setActiveCat] = useState('all');
    const [activeUC, setActiveUC] = useState('all');
    const [activeThreat, setActiveThreat] = useState('all');
    const [search, setSearch] = useState('');
    const [detail, setDetail] = useState(null);
    const [editing, setEditing] = useState(null);
    const [compareId, setCompareId] = useState('');

    const load = () => queryClient.invalidateQueries({ queryKey: QUERY_KEYS.competitions });

    // openDetail: show slim data instantly, then fetch the full row (with the
    // heavy Description / Strengths / Weaknesses / Positioning etc.) which the
    // list query no longer includes. Feels instant; the extra fields pop in
    // when the fetch completes (usually <300 ms).
    const openDetail = async (c) => {
        setDetail(c);
        try {
            const r = await fetch(`${API_URL}/competitions/${c.Id}`);
            if (!r.ok) return;
            const full = await r.json();
            setDetail(prev => (prev && prev.Id === c.Id ? { ...prev, ...full } : prev));
        } catch { /* keep slim data on failure */ }
    };

    const filtered = useMemo(() => {
        let list = competitors;
        if (activeCat !== 'all') list = list.filter(c => c.Category === activeCat);
        if (activeUC !== 'all') list = list.filter(c => (c.UseCases || []).includes(activeUC));
        if (activeThreat !== 'all') list = list.filter(c => c.Threat === activeThreat);
        if (search) {
            const q = search.toLowerCase();
            // Note: Tagline / Description / TechStack are no longer in the list
            // payload (fetched only on detail open), so search covers Name /
            // Category / Tags / HQ / Regions / Sectors — enough for finding a
            // competitor by identity, not for full-text hits inside descriptions.
            list = list.filter(c => `${c.Name || ''} ${c.Category || ''} ${(c.Tags || []).join(' ')} ${c.HQ || ''} ${c.Regions || ''} ${c.Sectors || ''}`.toLowerCase().includes(q));
        }
        return list;
    }, [competitors, activeCat, activeUC, activeThreat, search]);

    const stats = useMemo(() => {
        const t = competitors.length;
        const h = competitors.filter(c => c.Threat === 'high').length;
        const m = competitors.filter(c => c.Threat === 'medium').length;
        const cats = new Set(competitors.map(c => c.Category)).size;
        return { t, h, m, l: t - h - m, cats };
    }, [competitors]);

    const summary = useMemo(() => {
        const list = competitors; const total = list.length;
        const catCounts = {}; list.forEach(c => { catCounts[c.Category] = (catCounts[c.Category] || 0) + 1; });
        const topCats = Object.entries(catCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);
        const regionCounts = {};
        list.forEach(c => { if (c.Regions) { const r = c.Regions.split('(')[0].split(',')[0].trim(); regionCounts[r] = (regionCounts[r] || 0) + 1; } });
        const topRegions = Object.entries(regionCounts).sort((a, b) => b[1] - a[1]).slice(0, 5);
        let fundedCount = 0, unicorns = 0, publicCount = 0;
        list.forEach(c => {
            if (c.Funding && c.Funding !== 'Undisclosed' && c.Funding !== '') fundedCount++;
            const v = (c.Valuation || '').toLowerCase();
            if (['$1b', '$2b', '$8', '$40', '$200', '$100'].some(x => v.includes(x))) unicorns++;
            if (v.includes('public') || v.includes('nyse') || v.includes('nasdaq') || (c.Funding || '').toLowerCase().includes('public')) publicCount++;
        });
        const pct = (n) => total ? Math.round(n / total * 100) : 0;
        return {
            total, topCats, topRegions, fundedCount, unicorns, publicCount,
            pctHQ: pct(list.filter(c => c.HQ).length),
            pctFounded: pct(list.filter(c => c.Founded).length),
            pctTech: pct(list.filter(c => c.TechStack).length),
            pctDesc: pct(list.filter(c => c.Description).length),
        };
    }, [competitors]);

    const heroData = {
        dashboard: { title: 'Know Your', accent: 'Competition', sub: 'Comprehensive competitive intelligence across 78 companies in 13 categories. Battle cards, win themes, objection handlers, tech stacks, and architecture analysis — enriched with funding data, regional context, and deep research.' },
        compare: { title: 'Head-to-Head', accent: 'Comparison', sub: 'Select a competitor for detailed side-by-side analysis against ContexQ.' },
        matrix: { title: 'Capability', accent: 'Matrix', sub: 'Feature-by-feature comparison across the competitive landscape — Direct competitors.' },
        playbook: { title: 'Sales', accent: 'Playbook', sub: 'Strategic positioning, objection handlers, and value narratives for every competitive scenario.' },
        timeline: { title: 'Competitive', accent: 'Timeline', sub: 'Competitors ordered by founding year — covering region, tech stack, customer type, and target use cases.' },
    };

    async function handleSave(data) {
        try {
            const body = { ...data };
            if (data.Id) {
                await fetch(`${API_URL}/competitions/${data.Id}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
            } else {
                await fetch(`${API_URL}/competitions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
            }
            setEditing(null);
            await load();
        } catch (e) { alert('Save failed: ' + e.message); }
    }

    async function handleDelete(id) {
        if (!window.confirm('Delete this competitor permanently?')) return;
        try {
            await fetch(`${API_URL}/competitions/${id}`, { method: 'DELETE' });
            setEditing(null);
            setDetail(null);
            await load();
        } catch (e) { alert('Delete failed: ' + e.message); }
    }

    const hero = heroData[view] || heroData.dashboard;

    return (
        <div className="comp-root">
            <style>{CSS}</style>

            {/* Top nav */}
            <div className="comp-nav">
                <div className="comp-logo">
                    <span className="comp-logo-mark">CI</span>
                    <span>Competitive Intel</span>
                    <span className="comp-logo-sub">by ContexQ</span>
                </div>
                <div className="comp-nav-pills">
                    {['dashboard', 'compare', 'matrix', 'playbook', 'timeline'].map(v => (
                        <button key={v} className={view === v ? 'active' : ''} onClick={() => setView(v)}>
                            {v.charAt(0).toUpperCase() + v.slice(1)}
                        </button>
                    ))}
                </div>
                <div className="comp-search">
                    <input placeholder="Search competitors, capabilities, regions..." value={search} onChange={e => setSearch(e.target.value)} />
                </div>
            </div>

            <div className="comp-main">
                {/* Hero */}
                <div className="hero">
                    <h1>{hero.title} <span className="gradient">{hero.accent}</span></h1>
                    <p>{hero.sub}</p>
                    {view === 'dashboard' && (
                        <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                            <button className="btn-add" onClick={() => setEditing({ ...EMPTY })}>+ Add Competitor</button>
                            <button className="btn-ghost" onClick={load}>↻ Refresh</button>
                        </div>
                    )}
                </div>

                {loading && <div className="comp-empty">Loading competitors…</div>}

                {!loading && view === 'dashboard' && (
                    <>
                        {/* Summary dashboard */}
                        <div className="summary-grid">
                            <div className="summary-card">
                                <h3>📊 Category Distribution</h3>
                                {summary.topCats.map(([cat, cnt], i) => (
                                    <div key={cat}>
                                        <div className="summary-item"><span>{cat}</span><span className="summary-item-value">{cnt}</span></div>
                                        <div className="summary-bar"><div className="summary-bar-fill" style={{ width: `${Math.round(cnt / summary.total * 100)}%`, background: BAR_COLORS[i % BAR_COLORS.length] }} /></div>
                                    </div>
                                ))}
                            </div>
                            <div className="summary-card">
                                <h3>🌍 Top Regions</h3>
                                {summary.topRegions.map(([r, cnt], i) => (
                                    <div key={r}>
                                        <div className="summary-item"><span>{r}</span><span className="summary-item-value">{cnt}</span></div>
                                        <div className="summary-bar"><div className="summary-bar-fill" style={{ width: `${Math.round(cnt / summary.total * 100)}%`, background: BAR_COLORS[i % BAR_COLORS.length] }} /></div>
                                    </div>
                                ))}
                            </div>
                            <div className="summary-card">
                                <h3>💰 Market Intelligence</h3>
                                <div className="summary-item"><span>Total Tracked</span><span className="summary-item-value">{summary.total}</span></div>
                                <div className="summary-item"><span>With Funding Data</span><span className="summary-item-value">{summary.fundedCount}</span></div>
                                <div className="summary-item"><span>Public Companies</span><span className="summary-item-value">{summary.publicCount}</span></div>
                                <div className="summary-item"><span>Unicorns ($1B+)</span><span className="summary-item-value">{summary.unicorns}</span></div>
                            </div>
                            <div className="summary-card">
                                <h3>📋 Data Completeness</h3>
                                {[['HQ Location', summary.pctHQ], ['Founded Year', summary.pctFounded], ['Tech Stack', summary.pctTech], ['Description', summary.pctDesc]].map(([label, pct]) => (
                                    <div key={label}>
                                        <div className="summary-item"><span>{label}</span><span className="summary-item-value">{pct}%</span></div>
                                        <div className="summary-bar"><div className="summary-bar-fill" style={{ width: `${pct}%`, background: pct > 70 ? '#059669' : pct > 40 ? '#d97706' : '#dc2626' }} /></div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Stats strip */}
                        <div className="stats-strip">
                            <StatCard color="#059669" label="Tracked" value={stats.t} sub={`Across ${stats.cats} categories`} />
                            <StatCard color="#dc2626" label="Direct / High Threat" value={stats.h} sub="Primary competitors" />
                            <StatCard color="#d97706" label="Adjacent" value={stats.m} sub="Partial overlap / partners" />
                            <StatCard color="#2563eb" label="Indirect" value={stats.l} sub="Infrastructure / tools" />
                            <StatCard color="#7c3aed" label="ContexQ Dedup" value="25-30%" sub="vs 10-15% industry avg" />
                            <StatCard color="#0891b2" label="ER Accuracy" value="99%" sub="vs 85-90% industry avg" />
                            <StatCard color="#db2777" label="FP Reduction" value="40-60%" sub="via graph-native GNNs" />
                        </div>

                        {/* Filters */}
                        <div className="filter-block">
                            <div className="filter-row">
                                <div className="filter-row-label">Category</div>
                                {CATEGORIES.map(c => {
                                    const n = c.id === 'all' ? competitors.length : competitors.filter(x => x.Category === c.id).length;
                                    return <button key={c.id} className={`cat-tab ${activeCat === c.id ? 'active' : ''}`} onClick={() => setActiveCat(c.id)}>{c.label}<span className="cnt">{n}</span></button>;
                                })}
                            </div>
                            <div className="filter-row">
                                <div className="filter-row-label">Use Cases</div>
                                <button className={`cat-tab ${activeUC === 'all' ? 'active' : ''}`} onClick={() => setActiveUC('all')}>All</button>
                                {USE_CASES.map(u => <button key={u.id} className={`cat-tab ${activeUC === u.id ? 'active' : ''}`} onClick={() => setActiveUC(u.id)}><span className={`uc-tag ${u.cls}`} style={{ padding: 0, background: 'none', border: 'none', fontSize: 'inherit', textTransform: 'none', letterSpacing: 0 }}>{u.label}</span></button>)}
                            </div>
                            <div className="filter-row">
                                <div className="filter-row-label">Threat</div>
                                {THREAT_LEVELS.map(t => <button key={t.id} className={`cat-tab ${activeThreat === t.id ? 'active' : ''}`} onClick={() => setActiveThreat(t.id)}>{t.emoji} {t.label}</button>)}
                            </div>
                        </div>

                        {/* Grid */}
                        <div className="comp-grid">
                            {filtered.length === 0 && <div className="comp-empty">No competitors match your filters.</div>}
                            {filtered.map(c => <CompetitorCard key={c.Id} c={c} onClick={() => openDetail(c)} onEdit={(e) => { e.stopPropagation(); setEditing(c); }} />)}
                        </div>
                    </>
                )}

                {!loading && view === 'compare' && (
                    <CompareView competitors={competitors} selectedId={compareId} onSelect={setCompareId} />
                )}

                {!loading && view === 'matrix' && (
                    <MatrixView competitors={competitors} />
                )}

                {!loading && view === 'playbook' && (
                    <PlaybookView directCount={stats.h} />
                )}

                {!loading && view === 'timeline' && (
                    <TimelineView competitors={competitors} onClick={openDetail} />
                )}
            </div>

            {/* Detail overlay */}
            {detail && <DetailOverlay c={detail} onClose={() => setDetail(null)} onEdit={() => { setEditing(detail); setDetail(null); }} />}

            {/* Edit/Add modal */}
            {editing && <EditModal c={editing} onClose={() => setEditing(null)} onSave={handleSave} onDelete={editing.Id ? () => handleDelete(editing.Id) : null} />}
        </div>
    );
}

function StatCard({ color, label, value, sub }) {
    return (
        <div className="stat-card" style={{ borderTop: `3px solid ${color}` }}>
            <div className="stat-label">{label}</div>
            <div className="stat-value">{value}</div>
            <div className="stat-sub">{sub}</div>
        </div>
    );
}

function CompetitorCard({ c, onClick, onEdit }) {
    const tCls = c.Threat === 'high' ? 'threat-high' : c.Threat === 'medium' ? 'threat-medium' : 'threat-low';
    return (
        <div className="comp-card" onClick={onClick}>
            <button className="edit-btn" onClick={onEdit}>✎</button>
            <div className="comp-card-header">
                <div>
                    <div className="comp-name">{c.Name}</div>
                    <span className={`comp-type ${tCls}`}>{c.CompType || c.Threat}</span>
                </div>
                <div className="comp-card-cat">{c.Category}</div>
            </div>
            <div className="comp-meta">
                {c.HQ && <span>📍 {c.HQ}</span>}
                {c.Founded && <span>📅 {c.Founded}</span>}
                {c.Employees && <span>👥 {c.Employees}</span>}
                {c.Funding && <span>💰 {c.Funding}</span>}
            </div>
            <div className="comp-desc">{c.Tagline || c.Description || ''}</div>
            <div className="comp-uc-row">
                {(c.UseCases || []).map(u => {
                    const uc = USE_CASES.find(x => x.id === u);
                    return uc ? <span key={u} className={`uc-tag ${uc.cls}`}>{uc.label}</span> : null;
                })}
            </div>
            <div className="comp-bottom">
                <span>{c.Regions || ''}</span>
                {c.Valuation && <span style={{ fontWeight: 600 }}>Val: {c.Valuation}</span>}
            </div>
        </div>
    );
}

function DetailOverlay({ c, onClose, onEdit }) {
    const tCls = c.Threat === 'high' ? 'threat-high' : c.Threat === 'medium' ? 'threat-medium' : 'threat-low';
    const metaItems = [['Founded', c.Founded], ['HQ', c.HQ], ['Funding', c.Funding], ['Valuation', c.Valuation], ['Employees', c.Employees], ['Regions', c.Regions], ['Pricing', c.Pricing], ['Website', c.Website]].filter(([, v]) => v && v !== 'None');

    // Button is always visible. On click we attempt the download; if the
    // file isn't bundled for this competitor, we surface a "No information
    // available" inline message instead of failing silently.
    const [moreMsg, setMoreMsg] = useState('');     // user-facing message text
    const [moreBusy, setMoreBusy] = useState(false);

    const handleKnowMore = async () => {
        if (moreBusy) return;
        setMoreMsg(''); setMoreBusy(true);
        try {
            const res = await fetch(`${API_URL}/pitchbook-download?name=${encodeURIComponent(c.Name)}`);
            if (res.status === 404) {
                setMoreMsg('No information available for this company.');
                setTimeout(() => setMoreMsg(''), 4000);
                return;
            }
            if (!res.ok) {
                setMoreMsg(`Download failed (${res.status}). Try again.`);
                setTimeout(() => setMoreMsg(''), 4000);
                return;
            }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${c.Name}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (e) {
            setMoreMsg('Download failed. Check your connection.');
            setTimeout(() => setMoreMsg(''), 4000);
        } finally {
            setMoreBusy(false);
        }
    };

    return (
        <div className="detail-overlay" onClick={onClose}>
            <div className="detail-modal" onClick={e => e.stopPropagation()}>
                <div className="detail-topbar">
                    <button className="detail-back" onClick={onClose}>← Close</button>
                    <button className="btn-add" onClick={onEdit}>✎ Edit</button>
                </div>
                <div className="detail-header">
                    <div className="detail-title">{c.Name}</div>
                    <div className="detail-badges">
                        <span className={`comp-type ${tCls}`}>{c.CompType || c.Threat}</span>
                        <span className="comp-type" style={{ background: '#f7f8fa', color: '#6b7280' }}>{c.Category}</span>
                        {(c.UseCases || []).map(u => { const uc = USE_CASES.find(x => x.id === u); return uc ? <span key={u} className={`uc-tag ${uc.cls}`}>{uc.label}</span> : null; })}
                    </div>
                </div>
                <div className="detail-meta-grid">
                    {metaItems.map(([label, val]) => (
                        <div key={label} className="detail-meta-card">
                            <div className="detail-meta-label">{label}</div>
                            <div className="detail-meta-value">{label === 'Website' && val ? <a href={val.startsWith('http') ? val : `https://${val}`} target="_blank" rel="noreferrer">{val}</a> : val}</div>
                        </div>
                    ))}
                </div>

                <div style={{ marginTop: 6, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <button
                        type="button"
                        onClick={handleKnowMore}
                        disabled={moreBusy}
                        style={{
                            background: '#0c2340',
                            backgroundImage: 'linear-gradient(135deg,#0c2340 0%,#1e40af 60%,#2563eb 100%)',
                            color: '#ffffff', border: 'none',
                            padding: '10px 20px', borderRadius: 8,
                            fontSize: 13, fontWeight: 600,
                            cursor: moreBusy ? 'wait' : 'pointer',
                            opacity: moreBusy ? 0.7 : 1,
                            boxShadow: '0 2px 6px rgba(12,35,64,0.2)',
                            display: 'inline-flex', alignItems: 'center', gap: 8
                        }}
                    >
                        {moreBusy ? 'Preparing…' : '⬇ Know more (download)'}
                    </button>
                    {moreMsg && (
                        <span style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', padding: '6px 10px', borderRadius: 6 }}>
                            {moreMsg}
                        </span>
                    )}
                </div>
                {c.Description && <Section title="Overview"><p style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>{c.Description}</p></Section>}
                {c.TechStack && <div className="tech-box"><div className="detail-section-title" style={{ color: '#0369a1' }}>Tech Stack</div><div>{c.TechStack}</div></div>}
                {(c.FocusMarket || c.FocusUseCases || c.FocusSector) && (
                    <Section title="Target Focus">
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 8 }}>
                            {c.FocusMarket && <div className="detail-meta-card" style={{ borderLeft: '3px solid #2563eb' }}><div className="detail-meta-label">Focus Market</div><div className="detail-meta-value">{c.FocusMarket}</div></div>}
                            {c.FocusUseCases && <div className="detail-meta-card" style={{ borderLeft: '3px solid #7c3aed' }}><div className="detail-meta-label">Key Use Cases</div><div className="detail-meta-value">{c.FocusUseCases}</div></div>}
                            {c.FocusSector && <div className="detail-meta-card" style={{ borderLeft: '3px solid #059669' }}><div className="detail-meta-label">Target Sectors</div><div className="detail-meta-value">{c.FocusSector}</div></div>}
                        </div>
                    </Section>
                )}
                {((c.Strengths || []).length || (c.Weaknesses || []).length) && (
                    <div className="sw-grid">
                        {(c.Strengths || []).length > 0 && <div className="sw-card strengths"><h3>✓ Strengths</h3><ul>{c.Strengths.map((s, i) => <li key={i}>{s}</li>)}</ul></div>}
                        {(c.Weaknesses || []).length > 0 && <div className="sw-card weaknesses"><h3>✗ Weaknesses</h3><ul>{c.Weaknesses.map((w, i) => <li key={i}>{w}</li>)}</ul></div>}
                    </div>
                )}
                {c.ArchLimits && <div className="arch-box"><div className="detail-section-title" style={{ color: '#92400e' }}>Architecture Limitations</div><div>{c.ArchLimits}</div></div>}
                {(c.CustomerPain || []).length > 0 && <Section title="Customer & Market Pain Points">{c.CustomerPain.map((p, i) => <div key={i} className="pain-card">⚠️ {p}</div>)}</Section>}
                {c.Positioning && <div className="pos-box"><div className="detail-section-title" style={{ color: '#1d4ed8' }}>ContexQ Positioning</div><div>{c.Positioning}</div></div>}
                {(c.WinThemes || []).length > 0 && <Section title="Win Themes — How to Beat"><div className="win-themes">{c.WinThemes.map((w, i) => <div key={i} className="win-theme">🎯 {w}</div>)}</div></Section>}
                {(c.Objections || []).length > 0 && <Section title="Objection Handling">{c.Objections.map((o, i) => <ExpandableTP key={i} q={o.split('→')[0] || o} a={o.split('→')[1] || ''} />)}</Section>}
                {c.MatrixPos && <div style={{ marginTop: 12, padding: 12, background: '#f7f8fa', borderRadius: 8, fontSize: 12, color: '#6b7280' }}><strong>Matrix Position:</strong> {c.MatrixPos}</div>}
            </div>
        </div>
    );
}


function Section({ title, children }) {
    return (
        <div className="detail-section">
            <div className="detail-section-title">{title}</div>
            {children}
        </div>
    );
}

function ExpandableTP({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <div className={`tp ${open ? 'open' : ''}`} onClick={() => setOpen(!open)}>
            <div className="tp-q">{q}</div>
            {open && a && <div className="tp-a">{a}</div>}
        </div>
    );
}

function CompareView({ competitors, selectedId, onSelect }) {
    const c = competitors.find(x => String(x.Id) === String(selectedId));
    return (
        <div>
            <select className="compare-select" value={selectedId} onChange={e => onSelect(e.target.value)}>
                <option value="">Select Competitor</option>
                {competitors.map(x => <option key={x.Id} value={x.Id}>{x.Name} ({x.CompType || x.Category})</option>)}
            </select>
            {c && (
                <>
                    <table className="compare-table">
                        <thead><tr><th style={{ textAlign: 'left' }}>Capability</th><th className="cq">ContexQ</th><th>{c.Name}</th></tr></thead>
                        <tbody>
                            {COMPARE_ROWS.map(r => (
                                <tr key={r.label}><td>{r.label}</td><td className="cq-col">{r.contexq}</td><td>{r.check(c)}</td></tr>
                            ))}
                        </tbody>
                    </table>
                    {(c.WinThemes || []).length > 0 && (
                        <div style={{ marginTop: 24 }}>
                            <div className="detail-section-title">Win Themes vs {c.Name}</div>
                            <div className="win-themes">{c.WinThemes.map((w, i) => <div key={i} className="win-theme">🎯 {w}</div>)}</div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

function MatrixView({ competitors }) {
    const direct = competitors.filter(c => c.Threat === 'high').slice(0, 12);
    const cellCls = v => v === '✓' ? 'cap-yes' : v === '✗' ? 'cap-no' : 'cap-partial';
    return (
        <div style={{ overflowX: 'auto' }}>
            <table className="cap-table">
                <thead>
                    <tr>
                        <th style={{ minWidth: 140 }}>Capability</th>
                        <th className="cq-col" style={{ minWidth: 80 }}>ContexQ</th>
                        {direct.map(c => <th key={c.Id} style={{ minWidth: 70, fontSize: 10 }}>{c.Name}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {CAPABILITY_ROWS.map(row => (
                        <tr key={row.label}>
                            <td style={{ fontWeight: 600 }}>{row.label}</td>
                            <td className={cellCls(row.contexq)}>{row.contexq}</td>
                            {direct.map(c => { const v = row.check(c); return <td key={c.Id} className={cellCls(v)}>{v}</td>; })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function PlaybookView({ directCount }) {
    return (
        <div>
            <div className="uc-approach-grid">
                <div className="uc-approach-card point">
                    <h3>{PLAYBOOK.pointProblem.title}</h3>
                    <p>{PLAYBOOK.pointProblem.text}</p>
                    <ul>{PLAYBOOK.pointProblem.bullets.map((b, i) => <li key={i}>{b}</li>)}</ul>
                </div>
                <div className="uc-approach-card platform">
                    <h3>{PLAYBOOK.platform.title}</h3>
                    <p>{PLAYBOOK.platform.text}</p>
                    <ul>{PLAYBOOK.platform.bullets.map((b, i) => <li key={i} dangerouslySetInnerHTML={{ __html: b.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>') }} />)}</ul>
                </div>
            </div>
            <Section title="The Architecture Moat">
                <div className="moat-list">
                    {PLAYBOOK.architectureMoat.map(m => (
                        <div key={m.num} className="moat-item"><strong>{m.num} — {m.title}:</strong> {m.text}</div>
                    ))}
                </div>
            </Section>
            <Section title={`Competitive Scenarios (${directCount} Direct Competitors)`}>
                {PLAYBOOK.scenarios.map((s, i) => <ExpandableTP key={i} q={s.q} a={s.a} />)}
            </Section>
            <Section title="Value Metrics">
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 10 }}>
                    {PLAYBOOK.valueMetrics.map(m => (
                        <div key={m.label} className="stat-card" style={{ borderLeft: `3px solid ${m.color}` }}>
                            <div className="stat-label">{m.label}</div>
                            <div className="stat-value" style={{ color: m.color }}>{m.value}</div>
                            <div className="stat-sub">{m.sub}</div>
                        </div>
                    ))}
                </div>
            </Section>
        </div>
    );
}

function TimelineView({ competitors, onClick }) {
    const sorted = competitors
        .filter(c => c.Founded && /\d{4}/.test(c.Founded))
        .map(c => ({ ...c, year: parseInt(c.Founded.match(/\d{4}/)[0]) }))
        .sort((a, b) => a.year - b.year);
    const threatColors = { high: '#dc2626', medium: '#d97706', low: '#059669' };
    if (!sorted.length) return <div className="comp-empty">No competitors with founding dates.</div>;
    return (
        <div className="timeline-container">
            <div className="timeline-line" />
            {sorted.map(c => {
                const dot = threatColors[c.Threat] || '#9ca3af';
                const tCls = c.Threat === 'high' ? 'threat-high' : c.Threat === 'medium' ? 'threat-medium' : 'threat-low';
                return (
                    <div key={c.Id} className="timeline-item">
                        <div className="timeline-year">{c.Founded}</div>
                        <div className="timeline-dot" style={{ background: dot, boxShadow: `0 0 0 2px ${dot}40` }} />
                        <div className="timeline-card" onClick={() => onClick(c)} style={{ borderLeft: `3px solid ${dot}` }}>
                            <div className="timeline-card-header">
                                <div>
                                    <div className="timeline-card-name">{c.Name}</div>
                                    <div className="timeline-card-hq">{c.HQ || 'HQ not available'} · <span className={`comp-type ${tCls}`} style={{ fontSize: 9 }}>{c.CompType || c.Threat}</span></div>
                                </div>
                                <div style={{ textAlign: 'right', fontSize: 11, color: '#9ca3af' }}>{c.Category}</div>
                            </div>
                            <div style={{ marginBottom: 6 }}>
                                {(c.UseCases || []).map(u => { const uc = USE_CASES.find(x => x.id === u); return uc ? <span key={u} className={`uc-tag ${uc.cls}`}>{uc.label}</span> : null; })}
                            </div>
                            <div className="timeline-meta">
                                {(c.FocusMarket || c.Regions) && <div><div className="timeline-meta-label">Region / Market</div><div>{c.FocusMarket || c.Regions}</div></div>}
                                {c.TechStack && <div><div className="timeline-meta-label">Tech Stack</div><div>{c.TechStack}</div></div>}
                                {(c.FocusSector || c.Sectors) && <div><div className="timeline-meta-label">Target Sector</div><div>{c.FocusSector || c.Sectors}</div></div>}
                                {c.FocusUseCases && <div><div className="timeline-meta-label">Key Use Cases</div><div>{c.FocusUseCases}</div></div>}
                            </div>
                            {c.Valuation && <div style={{ marginTop: 6, fontSize: 11, color: '#9ca3af' }}>💰 {c.Funding || ''} · Val: {c.Valuation}</div>}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function EditModal({ c, onClose, onSave, onDelete }) {
    const [form, setForm] = useState({ ...c, UseCases: c.UseCases || [], Tags: c.Tags || [], Strengths: c.Strengths || [], Weaknesses: c.Weaknesses || [], CustomerPain: c.CustomerPain || [], WinThemes: c.WinThemes || [], Objections: c.Objections || [] });
    const upd = (k, v) => setForm({ ...form, [k]: v });
    const arrField = (k, label) => (
        <div className="form-field">
            <label>{label} (one per line)</label>
            <textarea rows={4} value={(form[k] || []).join('\n')} onChange={e => upd(k, e.target.value.split('\n').filter(Boolean))} />
        </div>
    );
    return (
        <div className="detail-overlay" onClick={onClose}>
            <div className="detail-modal" onClick={e => e.stopPropagation()}>
                <div className="detail-topbar">
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{c.Id ? 'Edit' : 'Add'} Competitor</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {onDelete && <button className="btn-danger" onClick={onDelete}>Delete</button>}
                        <button className="btn-ghost" onClick={onClose}>Cancel</button>
                        <button className="btn-add" onClick={() => onSave(form)}>Save</button>
                    </div>
                </div>
                <div className="form-grid">
                    <Field label="Name" val={form.Name} onChange={v => upd('Name', v)} />
                    <Field label="Category" val={form.Category} onChange={v => upd('Category', v)} select={CATEGORIES.filter(c => c.id !== 'all').map(c => c.id)} />
                    <Field label="Threat" val={form.Threat} onChange={v => upd('Threat', v)} select={['high', 'medium', 'low']} />
                    <Field label="Comp Type" val={form.CompType} onChange={v => upd('CompType', v)} select={['Direct', 'Adjacent', 'Indirect']} />
                    <Field label="Tagline" val={form.Tagline} onChange={v => upd('Tagline', v)} />
                    <Field label="Approach" val={form.Approach} onChange={v => upd('Approach', v)} select={['point', 'platform']} />
                    <Field label="Founded" val={form.Founded} onChange={v => upd('Founded', v)} />
                    <Field label="HQ" val={form.HQ} onChange={v => upd('HQ', v)} />
                    <Field label="Funding" val={form.Funding} onChange={v => upd('Funding', v)} />
                    <Field label="Valuation" val={form.Valuation} onChange={v => upd('Valuation', v)} />
                    <Field label="Employees" val={form.Employees} onChange={v => upd('Employees', v)} />
                    <Field label="Regions" val={form.Regions} onChange={v => upd('Regions', v)} />
                    <Field label="Pricing" val={form.Pricing} onChange={v => upd('Pricing', v)} />
                    <Field label="Website" val={form.Website} onChange={v => upd('Website', v)} />
                    <Field label="Sectors" val={form.Sectors} onChange={v => upd('Sectors', v)} />
                    <Field label="Tech Stack" val={form.TechStack} onChange={v => upd('TechStack', v)} />
                    <Field label="Focus Market" val={form.FocusMarket} onChange={v => upd('FocusMarket', v)} />
                    <Field label="Focus Use Cases" val={form.FocusUseCases} onChange={v => upd('FocusUseCases', v)} />
                    <Field label="Focus Sector" val={form.FocusSector} onChange={v => upd('FocusSector', v)} />
                    <Field label="Matrix Position" val={form.MatrixPos} onChange={v => upd('MatrixPos', v)} />
                </div>
                <div className="form-field" style={{ marginTop: 12 }}>
                    <label>Use Case Tags</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {USE_CASES.map(u => (
                            <button key={u.id} type="button" className={`cat-tab ${(form.UseCases || []).includes(u.id) ? 'active' : ''}`}
                                onClick={() => upd('UseCases', (form.UseCases || []).includes(u.id) ? form.UseCases.filter(x => x !== u.id) : [...(form.UseCases || []), u.id])}>
                                {u.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="form-field">
                    <label>Description</label>
                    <textarea rows={3} value={form.Description || ''} onChange={e => upd('Description', e.target.value)} />
                </div>
                <div className="form-field">
                    <label>Positioning</label>
                    <textarea rows={2} value={form.Positioning || ''} onChange={e => upd('Positioning', e.target.value)} />
                </div>
                <div className="form-field">
                    <label>Architecture Limitations</label>
                    <textarea rows={2} value={form.ArchLimits || ''} onChange={e => upd('ArchLimits', e.target.value)} />
                </div>
                {arrField('Strengths', 'Strengths')}
                {arrField('Weaknesses', 'Weaknesses')}
                {arrField('CustomerPain', 'Customer Pain')}
                {arrField('WinThemes', 'Win Themes')}
                {arrField('Objections', 'Objections (use → to split question/answer)')}
                {arrField('Tags', 'Tags')}
            </div>
        </div>
    );
}

function Field({ label, val, onChange, select }) {
    return (
        <div className="form-field">
            <label>{label}</label>
            {select
                ? <select value={val || ''} onChange={e => onChange(e.target.value)}>
                    <option value="">—</option>
                    {select.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
                : <input value={val || ''} onChange={e => onChange(e.target.value)} />}
        </div>
    );
}

const CSS = `
.comp-root { font-family: 'Plus Jakarta Sans', system-ui, sans-serif; color: #111827; background: #fafafa; min-height: 100vh; padding-bottom: 40px; }
.moat-list { display: flex; flex-direction: column; gap: 10px; }
.moat-item { background: #f5f5f7; border-radius: 10px; padding: 14px 16px; font-size: 13px; line-height: 1.6; color: #374151; }
.moat-item strong { color: #111827; }
.comp-nav { position: sticky; top: 0; z-index: 10; background: rgba(255,255,255,.95); backdrop-filter: blur(16px); border-bottom: 1px solid #e5e7eb; padding: 0 24px; display: flex; align-items: center; gap: 20px; height: 60px; }
.comp-logo { display: flex; align-items: center; gap: 10px; font-weight: 800; font-size: 16px; letter-spacing: -0.02em; }
.comp-logo-mark { width: 30px; height: 30px; background: linear-gradient(135deg, #2563eb, #7c3aed); border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 12px; color: #fff; }
.comp-logo-sub { color: #9ca3af; font-weight: 400; font-size: 13px; }
.comp-nav-pills { display: flex; gap: 4px; flex: 1; }
.comp-nav-pills button { background: none; border: none; font-family: inherit; font-size: 12px; font-weight: 500; padding: 6px 12px; border-radius: 6px; cursor: pointer; color: #6b7280; transition: all 0.15s; }
.comp-nav-pills button:hover { background: rgba(0,0,0,0.03); color: #111827; }
.comp-nav-pills button.active { color: #2563eb; background: rgba(37,99,235,0.08); font-weight: 600; }
.comp-search { width: 300px; }
.comp-search input { width: 100%; background: #f7f8fa; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 14px; font-family: inherit; font-size: 13px; outline: none; }
.comp-search input:focus { border-color: #2563eb; background: #fff; }

.comp-main { max-width: 1480px; margin: 0 auto; padding: 28px 32px; }
.hero h1 { font-size: 28px; font-weight: 800; letter-spacing: -0.03em; line-height: 1.2; margin-bottom: 6px; }
.hero .gradient { background: linear-gradient(135deg, #2563eb, #7c3aed); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
.hero p { color: #6b7280; font-size: 14px; max-width: 700px; line-height: 1.6; margin-bottom: 16px; }
.btn-add { background: #2563eb; color: #fff; border: none; padding: 8px 14px; border-radius: 8px; font-family: inherit; font-size: 12px; font-weight: 600; cursor: pointer; }
.btn-add:hover { background: #1d4ed8; }
.btn-ghost { background: #fff; color: #6b7280; border: 1px solid #e5e7eb; padding: 8px 14px; border-radius: 8px; font-family: inherit; font-size: 12px; font-weight: 500; cursor: pointer; }
.btn-danger { background: #dc2626; color: #fff; border: none; padding: 8px 14px; border-radius: 8px; font-family: inherit; font-size: 12px; font-weight: 600; cursor: pointer; }

.summary-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(250px, 1fr)); gap: 12px; margin: 24px 0; }
.summary-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px 16px; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
.summary-card h3 { font-size: 13px; font-weight: 700; margin-bottom: 12px; letter-spacing: -0.01em; }
.summary-item { display: flex; justify-content: space-between; align-items: center; font-size: 12px; color: #6b7280; margin-bottom: 4px; }
.summary-item-value { font-weight: 700; color: #111827; }
.summary-bar { width: 100%; height: 4px; background: #f7f8fa; border-radius: 2px; overflow: hidden; margin-bottom: 8px; }
.summary-bar-fill { height: 100%; border-radius: 2px; transition: width 0.4s ease; }

.stats-strip { display: grid; grid-template-columns: repeat(auto-fit, minmax(155px, 1fr)); gap: 10px; margin-bottom: 24px; }
.stat-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 14px 16px; box-shadow: 0 1px 2px rgba(0,0,0,0.04); position: relative; }
.stat-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #9ca3af; margin-bottom: 4px; }
.stat-value { font-size: 22px; font-weight: 800; letter-spacing: -0.02em; }
.stat-sub { font-size: 11px; color: #6b7280; margin-top: 2px; }

.filter-block { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px 16px; margin-bottom: 16px; }
.filter-row { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 8px; align-items: center; }
.filter-row:last-child { margin-bottom: 0; }
.filter-row-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #9ca3af; min-width: 70px; }
.cat-tab { background: #fff; border: 1px solid #e5e7eb; border-radius: 7px; padding: 4px 10px; font-size: 11px; font-weight: 500; color: #6b7280; cursor: pointer; font-family: inherit; }
.cat-tab:hover { border-color: #d1d5db; color: #111827; }
.cat-tab.active { border-color: #2563eb; color: #2563eb; background: rgba(37,99,235,0.08); font-weight: 600; }
.cat-tab .cnt { font-size: 10px; opacity: 0.6; margin-left: 4px; }

.uc-tag { display: inline-flex; align-items: center; gap: 4px; font-size: 10px; font-weight: 600; padding: 2px 6px; border-radius: 5px; text-transform: uppercase; letter-spacing: 0.04em; margin-right: 4px; }
.uc-fincrime { color: #7c2d12; background: #fff7ed; border: 1px solid #fed7aa; }
.uc-fraud { color: #7f1d1d; background: #fef2f2; border: 1px solid #fecaca; }
.uc-esg { color: #14532d; background: #f0fdf4; border: 1px solid #bbf7d0; }
.uc-supply { color: #1e3a5f; background: #eff6ff; border: 1px solid #bfdbfe; }
.uc-kyc { color: #581c87; background: #faf5ff; border: 1px solid #e9d5ff; }
.uc-ci { color: #155e75; background: #ecfeff; border: 1px solid #a5f3fc; }

.comp-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 12px; }
.comp-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 16px; cursor: pointer; transition: all 0.2s; position: relative; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
.comp-card:hover { border-color: #2563eb; box-shadow: 0 4px 12px rgba(0,0,0,0.07); transform: translateY(-1px); }
.edit-btn { position: absolute; top: 8px; right: 8px; background: #f7f8fa; border: 1px solid #e5e7eb; border-radius: 6px; font-size: 11px; padding: 3px 8px; cursor: pointer; opacity: 0; transition: opacity 0.15s; }
.comp-card:hover .edit-btn { opacity: 1; }
.comp-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 10px; }
.comp-name { font-size: 15px; font-weight: 700; letter-spacing: -0.01em; }
.comp-card-cat { font-size: 10px; color: #9ca3af; }
.comp-type { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; padding: 2px 6px; border-radius: 4px; margin-top: 3px; display: inline-block; }
.threat-high { color: #dc2626; background: #fef2f2; }
.threat-medium { color: #d97706; background: #fffbeb; }
.threat-low { color: #059669; background: #ecfdf5; }
.comp-meta { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; font-size: 11px; color: #6b7280; }
.comp-desc { font-size: 12px; color: #6b7280; line-height: 1.5; margin-bottom: 10px; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.comp-uc-row { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 8px; }
.comp-bottom { display: flex; justify-content: space-between; font-size: 11px; color: #9ca3af; padding-top: 8px; border-top: 1px solid #f3f4f6; }
.comp-empty { text-align: center; padding: 40px; color: #9ca3af; font-size: 14px; }

.detail-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 100; display: flex; justify-content: center; align-items: flex-start; padding: 32px 16px; overflow-y: auto; }
.detail-modal { background: #fff; border-radius: 16px; width: 100%; max-width: 960px; padding: 28px; box-shadow: 0 20px 60px rgba(0,0,0,0.3); }
.detail-topbar { display: flex; justify-content: space-between; margin-bottom: 18px; }
.detail-back { background: none; border: none; color: #6b7280; font-size: 13px; cursor: pointer; }
.detail-header { margin-bottom: 18px; border-bottom: 1px solid #f3f4f6; padding-bottom: 14px; }
.detail-title { font-size: 26px; font-weight: 800; letter-spacing: -0.02em; margin-bottom: 8px; }
.detail-badges { display: flex; flex-wrap: wrap; gap: 6px; }
.detail-meta-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); gap: 8px; margin-bottom: 20px; }
.detail-meta-card { background: #f7f8fa; border-radius: 8px; padding: 10px 12px; }
.detail-meta-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: #9ca3af; margin-bottom: 3px; }
.detail-meta-value { font-size: 13px; font-weight: 600; color: #111827; }
.detail-section { margin: 18px 0; }
.detail-section-title { font-size: 14px; font-weight: 700; margin-bottom: 10px; }
.tech-box { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 10px; padding: 14px; margin: 12px 0; font-size: 13px; }
.arch-box { background: #fffbeb; border: 1px solid #fde68a; border-radius: 10px; padding: 14px; margin: 12px 0; font-size: 13px; }
.pos-box { background: #eff6ff; border: 1px solid #bfdbfe; border-radius: 10px; padding: 14px; margin: 12px 0; font-size: 13px; }
.sw-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 12px 0; }
.sw-card { border-radius: 10px; padding: 14px; }
.sw-card.strengths { background: #ecfdf5; border: 1px solid #bbf7d0; }
.sw-card.weaknesses { background: #fef2f2; border: 1px solid #fecaca; }
.sw-card h3 { font-size: 12px; font-weight: 700; margin-bottom: 8px; }
.sw-card ul { padding-left: 18px; font-size: 12px; color: #374151; line-height: 1.6; }
.pain-card { background: #fff7ed; border-left: 3px solid #f97316; padding: 10px 12px; border-radius: 6px; font-size: 12px; margin-bottom: 6px; }
.win-themes { display: flex; flex-direction: column; gap: 6px; }
.win-theme { background: #eff6ff; border-left: 3px solid #2563eb; padding: 10px 12px; border-radius: 6px; font-size: 12px; line-height: 1.5; }
.tp { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; margin-bottom: 8px; cursor: pointer; overflow: hidden; transition: all 0.15s; }
.tp:hover { border-color: #d1d5db; }
.tp.open { border-color: #2563eb; background: #fafbff; }
.tp-q { padding: 12px 16px; font-size: 13px; font-weight: 600; color: #111827; }
.tp-a { padding: 0 16px 14px; font-size: 12px; color: #6b7280; line-height: 1.6; }

.compare-select { width: 100%; max-width: 400px; padding: 10px 14px; font-family: inherit; font-size: 13px; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; margin-bottom: 16px; }
.compare-table, .cap-table { width: 100%; border-collapse: collapse; background: #fff; border-radius: 10px; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,0.04); }
.compare-table th, .compare-table td, .cap-table th, .cap-table td { padding: 10px 12px; border-bottom: 1px solid #f3f4f6; font-size: 12px; text-align: center; }
.compare-table th, .cap-table th { background: #f7f8fa; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7280; }
.compare-table .cq, .cap-table .cq-col { color: #2563eb; }
.compare-table td:first-child, .cap-table td:first-child { text-align: left; }
.cap-yes { color: #059669; font-weight: 700; background: #ecfdf5; }
.cap-no { color: #dc2626; background: #fef2f2; }
.cap-partial { color: #d97706; background: #fffbeb; }

.uc-approach-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 20px; }
.uc-approach-card { border-radius: 12px; padding: 18px; }
.uc-approach-card.point { background: #f7f8fa; border: 1px solid #e5e7eb; }
.uc-approach-card.platform { background: linear-gradient(135deg, #eff6ff, #faf5ff); border: 1px solid #bfdbfe; }
.uc-approach-card h3 { font-size: 14px; font-weight: 700; margin-bottom: 8px; }
.uc-approach-card p { font-size: 12px; color: #6b7280; line-height: 1.6; margin-bottom: 10px; }
.uc-approach-card ul { padding-left: 18px; font-size: 12px; color: #374151; line-height: 1.8; }

.timeline-container { position: relative; padding: 20px 0; }
.timeline-line { position: absolute; left: 80px; top: 0; bottom: 0; width: 2px; background: #e5e7eb; }
.timeline-item { display: flex; gap: 16px; margin-bottom: 20px; align-items: flex-start; }
.timeline-year { min-width: 60px; text-align: right; font-size: 12px; font-weight: 700; color: #6b7280; padding-top: 10px; }
.timeline-dot { width: 14px; height: 14px; border-radius: 50%; margin-top: 12px; position: relative; z-index: 1; }
.timeline-card { flex: 1; background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: 14px; cursor: pointer; transition: all 0.15s; }
.timeline-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.07); }
.timeline-card-header { display: flex; justify-content: space-between; margin-bottom: 6px; }
.timeline-card-name { font-size: 15px; font-weight: 700; }
.timeline-card-hq { font-size: 11px; color: #6b7280; margin-top: 3px; }
.timeline-meta { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: 6px; margin-top: 8px; }
.timeline-meta > div { background: #f7f8fa; border-radius: 6px; padding: 6px 8px; font-size: 11px; }
.timeline-meta-label { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: #9ca3af; margin-bottom: 2px; }

.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.form-field { display: flex; flex-direction: column; gap: 4px; margin-bottom: 10px; }
.form-field label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em; color: #6b7280; }
.form-field input, .form-field select, .form-field textarea { padding: 8px 10px; font-family: inherit; font-size: 12px; border: 1px solid #e5e7eb; border-radius: 6px; outline: none; }
.form-field input:focus, .form-field select:focus, .form-field textarea:focus { border-color: #2563eb; }
`;
